import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    OnInit,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { QueryObserverResult } from '@tanstack/query-core';
import { AsyncPipe } from '@angular/common';
import { BehaviorSubject, filter, Observable, switchMap, take } from 'rxjs';
import { LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { StateProvince } from '../../models/location/state-province';
import { LocationDataService } from '../../services/location-data.service';
import { VirtualizationSettings } from '@progress/kendo-angular-dropdowns';

@Component({
    selector: 'app-states-and-provinces-filter',
    templateUrl: './states-and-provinces-filter.component.html',
    styleUrls: ['./states-and-provinces-filter.component.scss'],
    imports: [AsyncPipe, LabelModule, DropDownsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => StatesAndProvincesFilterComponent),
            multi: true,
        },
    ],
})
export class StatesAndProvincesFilterComponent
    implements ControlValueAccessor, OnInit
{
    private locationDataService = inject(LocationDataService);
    private cdr = inject(ChangeDetectorRef);

    public statesProvincesQueryResults$?: Observable<
        QueryObserverResult<StateProvince[], Error>
    >;

    public isDisabled: boolean = false;
    public value: string[] = [];
    public virtual: VirtualizationSettings = { itemHeight: 28 };

    private onChange: (value: string[]) => void = () => {};
    private onTouched: () => void = () => {};

    private subRegionsSubject = new BehaviorSubject<string[]>([]);

    @Input() set subRegions(value: string[]) {
        this.subRegionsSubject.next(value ?? []);
    }

    writeValue(value: string[]): void {
        this.value = value ?? [];
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (value: string[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    handleValueChange(value: string[]): void {
        this.value = value;
        this.onChange(value);
    }

    handleBlur(): void {
        this.onTouched();
    }

    ngOnInit(): void {
        this.statesProvincesQueryResults$ =
            this.locationDataService.dataUpdated$.pipe(
                filter((updated) => updated),
                take(1),
                switchMap(() => this.subRegionsSubject),
                switchMap(
                    (subRegions) =>
                        this.locationDataService.getStatesProvinces(
                            '',
                            subRegions,
                        ).result$,
                ),
            );
    }
}
